import React, { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import logo from '../static/Crunch Time Jobs.png';
import background from '../static/login_background.jpg';
import '../static/user.css';
import { url } from "../Components/url";
import { Spinner } from "react-bootstrap";

function passwordClientError(password) {
    if (!password || password.length < 8) {
        return "Password must be at least 8 characters.";
    }
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
        return "Password must include a letter and a number.";
    }
    return "";
}

function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = useMemo(() => {
        const raw = searchParams.get("token") || "";
        try {
            return decodeURIComponent(raw.trim());
        } catch {
            return raw.trim();
        }
    }, [searchParams]);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [username, setUsername] = useState('');
    const [error, setError] = useState('');
    const [validToken, setValidToken] = useState(!!token);

    useEffect(() => {
        setValidToken(!!token);
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setUsername('');
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        const clientError = passwordClientError(password);
        if (clientError) {
            setError(clientError);
            return;
        }
        try {
            setIsLoading(true);
            const response = await fetch(`${url}/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // Public endpoint — do not send auth cookies (avoids CSRF edge cases).
                credentials: 'omit',
                body: JSON.stringify({ token, password }),
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                const uname = data.username || '';
                setUsername(uname);
                setMessage(
                    uname
                        ? `Password reset successful. Sign in with username “${uname}”.`
                        : 'Password reset successful. You can now sign in with your username.'
                );
                setPassword('');
                setConfirmPassword('');
            } else {
                setError(data.message || data.error || 'Invalid or expired reset link.');
            }
        } catch (err) {
            setError('Could not reach the server. Check that the API is running and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!validToken) {
        return (
            <div className="login-container">
                <div className="login-background"><img src={background} alt="Background" /></div>
                <div className="login-content">
                    <div className="login-card">
                        <div className="login-header">
                            <img src={logo} alt="Logo" className="login-logo" />
                            <h1 className="login-title">Invalid link</h1>
                            <p className="login-subtitle">This reset link is missing or invalid.</p>
                        </div>
                        <div className="login-body">
                            <Link to="/forgot-password" className="btn btn-primary" style={{ width: '100%' }}>Request new link</Link>
                            <p className="mt-3 text-center"><Link to="/" className="login-link">Back to sign in</Link></p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <div className="login-background">
                <img src={background} alt="Background" />
            </div>
            <div className="login-content">
                <div className="login-card">
                    <div className="login-header">
                        <img src={logo} alt="Logo" className="login-logo" />
                        <h1 className="login-title">Set new password</h1>
                        <p className="login-subtitle">
                            At least 8 characters, including a letter and a number
                        </p>
                    </div>
                    <form onSubmit={handleSubmit} className="login-body">
                        <div className="login-form-group">
                            <label htmlFor="password" className="login-form-label">New password</label>
                            <input
                                type="password"
                                id="password"
                                className="login-form-control"
                                placeholder="Enter new password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={8}
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="login-form-group">
                            <label htmlFor="confirmPassword" className="login-form-label">Confirm password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                className="login-form-control"
                                placeholder="Confirm new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                minLength={8}
                                autoComplete="new-password"
                            />
                        </div>
                        {message && (
                            <div className="alert alert-success mt-2" style={{ fontSize: '0.875rem' }}>
                                {message}
                                {username ? (
                                    <div className="mt-2">
                                        <Link to="/" className="login-link">Go to sign in</Link>
                                    </div>
                                ) : null}
                            </div>
                        )}
                        {error && (
                            <div className="login-error d-flex align-items-center mt-2 text-danger" style={{ fontSize: '0.875rem' }}>{error}</div>
                        )}
                        <button
                            type="submit"
                            className="btn btn-primary mt-3"
                            style={{ width: '100%', padding: '0.75rem' }}
                            disabled={isLoading || !!message}
                        >
                            {isLoading ? <Spinner animation="border" size="sm" /> : 'Reset password'}
                        </button>
                        <p className="mt-3 text-center">
                            <Link to="/" className="login-link">Back to sign in</Link>
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;
